import {
  ArrowRight,
  Baby,
  Backpack,
  Bird,
  Bone,
  Briefcase,
  CalendarDays,
  Car,
  Cat,
  Check,
  ChevronDown,
  CircleHelp,
  Clock,
  Dog,
  DoorOpen,
  Fence,
  FileCheck,
  Fish,
  HandHeart,
  Heart,
  House,
  KeyRound,
  Mail,
  MapPin,
  MessageCircle,
  Moon,
  Mountain,
  PawPrint,
  PersonStanding,
  Phone,
  Rabbit,
  Rat,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sofa,
  Sprout,
  Stethoscope,
  Store,
  Sun,
  Trees,
  Turtle,
  UserRound,
  Users,
  UsersRound,
  X,
  type LucideIcon,
} from "lucide-react";

// Het icoonvocabulaire van DierMatch, apart van het scherm dat het als eerste gebruikte.
//
// Stond in `DierMatchSite.tsx`; verhuisd toen de matchwizard (epic 12) dezelfde iconen nodig had.
// Daar importeren uit `DierMatchSite` zou een kring opleveren — dat bestand rendert de wizard — en
// een kring die toevallig werkt omdat functiedeclaraties gehesen worden, is geen fundament.

/**
 * De iconen, als lijntekeningen in de tekstkleur.
 *
 * <p>Inline SVG en geen emoji: een emoji wordt door het besturingssysteem getekend, dus op Windows
 * krijg je een ander plaatje dan op een Mac en op Android weer een derde — in kleur, in dikte en in
 * grootte. Op een pagina die het van rust moet hebben valt dat meteen op. Deze tekeningen zijn overal
 * gelijk, schalen mee en nemen de kleur van hun omgeving over.
 */
const LUCIDE_ICONEN: Record<string, LucideIcon> = {
  poot: PawPrint,
  speld: MapPin,
  kalender: CalendarDays,
  zoek: Search,
  schild: ShieldCheck,
  hart: Heart,
  mensen: UsersRound,
  huis: House,
  praatwolk: MessageCircle,
  envelop: Mail,
  schuifjes: SlidersHorizontal,
  pijlRechts: ArrowRight,
  stethoscoop: Stethoscope,
  match: UsersRound,
  nazorg: HandHeart,
  chevron: ChevronDown,

  // ── Keuzetegels: elk antwoord zijn eigen plaatje ──────────────────────────────────────────────
  // Zes keer hetzelfde huisje naast elkaar is geen illustratie maar behang: je oog slaat de rij
  // over en leest alsnog alleen de woorden. Een tegel met een eigen tekening is in één oogopslag
  // uit elkaar te houden, en dat is precies wat een keuzeraster moet doen.
  hond: Dog,
  kat: Cat,
  konijn: Rabbit,
  knaagdier: Rat,
  vogel: Bird,
  vis: Fish,
  schildpad: Turtle,
  bot: Bone,

  persoon: UserRound,
  stel: UsersRound,
  groep: Users,
  gezin: Baby,
  kind: PersonStanding,

  tuin: Trees,
  kleinetuin: Sprout,
  hek: Fence,
  buitengebied: Mountain,
  dorp: Store,
  binnen: DoorOpen,
  bank: Sofa,
  tiener: Backpack,
  sleutel: KeyRound,
  papier: FileCheck,

  auto: Car,
  klok: Clock,
  werk: Briefcase,
  dag: Sun,
  nacht: Moon,

  telefoon: Phone,

  ja: Check,
  nee: X,
  vraagteken: CircleHelp,
};

export function Icoon({ naam, size = 20 }: { naam: string; size?: number }) {
  const LucideIcoon = LUCIDE_ICONEN[naam];
  if (LucideIcoon) {
    return <LucideIcoon width={size} height={size} strokeWidth={1.8} aria-hidden="true" />;
  }

  const gedeeld = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
  };
  switch (naam) {
    case "poot":
      return (
        <svg {...gedeeld} fill="currentColor" stroke="none">
          <ellipse cx="6.6" cy="10.5" rx="1.7" ry="2.2" />
          <ellipse cx="17.4" cy="10.5" rx="1.7" ry="2.2" />
          <ellipse cx="9.7" cy="7" rx="1.6" ry="2.1" />
          <ellipse cx="14.3" cy="7" rx="1.6" ry="2.1" />
          <path d="M12 13.6c-2.4 0-4.4 1.7-4.4 3.8 0 1.4 1.1 2.3 2.5 2.3.8 0 1.3-.2 1.9-.4.6.2 1.1.4 1.9.4 1.4 0 2.5-.9 2.5-2.3 0-2.1-2-3.8-4.4-3.8Z" />
        </svg>
      );
    case "speld":
      return (
        <svg {...gedeeld}>
          <path d="M12 21s7-5.2 7-10.4A7 7 0 0 0 5 10.6C5 15.8 12 21 12 21Z" />
          <circle cx="12" cy="10.4" r="2.6" />
        </svg>
      );
    case "kalender":
      return (
        <svg {...gedeeld}>
          <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
          <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
        </svg>
      );
    case "zoek":
      return (
        <svg {...gedeeld}>
          <circle cx="11" cy="11" r="6.5" />
          <path d="m16 16 4.5 4.5" />
        </svg>
      );
    case "schild":
      return (
        <svg {...gedeeld}>
          <path d="M12 3 5 5.8v5.4c0 4.4 2.9 8.1 7 9.3 4.1-1.2 7-4.9 7-9.3V5.8L12 3Z" />
          <path d="m9 12 2.2 2.2L15.4 10" />
        </svg>
      );
    case "hart":
      return (
        <svg {...gedeeld}>
          <path d="M12 20s-7.2-4.4-7.2-9.3A4.1 4.1 0 0 1 12 8.2a4.1 4.1 0 0 1 7.2 2.5C19.2 15.6 12 20 12 20Z" />
        </svg>
      );
    case "mensen":
      return (
        <svg {...gedeeld}>
          <circle cx="9" cy="8.5" r="3" />
          <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
          <path d="M16 6.2a3 3 0 0 1 0 5.8M17.5 14.4a5.5 5.5 0 0 1 3 5.1" />
        </svg>
      );
    case "huis":
      return (
        <svg {...gedeeld}>
          <path d="M4 10.6 12 4l8 6.6" />
          <path d="M6 9.8V20h12V9.8" />
        </svg>
      );
    case "praatwolk":
      return (
        <svg {...gedeeld}>
          <path d="M20 12.4c0 3.5-3.6 6.3-8 6.3-.9 0-1.8-.1-2.6-.3L4 20l1.3-3.4A6.1 6.1 0 0 1 4 12.4C4 8.9 7.6 6 12 6s8 2.9 8 6.4Z" />
        </svg>
      );
    case "stethoscoop":
      return (
        <svg {...gedeeld}>
          <path d="M6 3v5.2a4.2 4.2 0 0 0 8.4 0V3M4.5 3h3M12.9 3h3" />
          <path d="M10.2 12.1v2.3a5 5 0 0 0 10 0v-1.1" />
          <circle cx="20.2" cy="11.4" r="1.8" />
        </svg>
      );
    case "match":
      return (
        <svg {...gedeeld}>
          <circle cx="7.5" cy="7" r="2.5" />
          <circle cx="16.5" cy="7" r="2.5" />
          <path d="M3.5 17.5v-1.2a4 4 0 0 1 4-4h1M20.5 17.5v-1.2a4 4 0 0 0-4-4h-1" />
          <path d="M12 19s-3.2-2-3.2-4.1A1.9 1.9 0 0 1 12 13.5a1.9 1.9 0 0 1 3.2 1.4C15.2 17 12 19 12 19Z" />
        </svg>
      );
    case "nazorg":
      return (
        <svg {...gedeeld}>
          <path d="M12 11s-3.6-2.2-3.6-4.7A2.2 2.2 0 0 1 12 4.7a2.2 2.2 0 0 1 3.6 1.6C15.6 8.8 12 11 12 11Z" />
          <path d="M4 13.5c2.2 0 3.4 1 4.4 2.6l1.3 2.1M20 13.5c-2.2 0-3.4 1-4.4 2.6l-1.3 2.1" />
          <path d="M3 11.5v5.3L7.8 21M21 11.5v5.3L16.2 21" />
        </svg>
      );
    // ── de woningen ────────────────────────────────────────────────────────────────────────────
    // Zes woningsoorten op een rij, dus moet je ze náást elkaar uit elkaar kunnen houden. Lucide
    // heeft maar één huis, en zes keer hetzelfde huis zegt niets. Daarom één familie tekeningen op
    // hetzelfde stramien -- zelfde grondlijn, zelfde dakhoek, zelfde deur -- die alleen verschillen
    // in wat de woningsoort ís: hoeveel buren er aan vast zitten, en hoe het dak loopt.
    case "appartement":
      return (
        <svg {...gedeeld}>
          <path d="M6.5 20V4.5h11V20" />
          <path d="M3 20h18" />
          <path d="M9.3 7.6h1.6M13.1 7.6h1.6M9.3 11.2h1.6M13.1 11.2h1.6M9.3 14.8h1.6M13.1 14.8h1.6" />
          <path d="M10.6 20v-2.4h2.8V20" />
        </svg>
      );
    case "tussenwoning":
      return (
        <svg {...gedeeld}>
          <path d="M2.5 20h19" />
          <path d="M2.5 13.6h4.6V20M21.5 13.6h-4.6V20" />
          <path d="M6.1 12.2 12 7.4l5.9 4.8" />
          <path d="M7.1 11.4V20h9.8v-8.6" />
          <path d="M10.7 20v-3.4h2.6V20" />
        </svg>
      );
    case "hoekwoning":
      return (
        <svg {...gedeeld}>
          <path d="M2.5 20h19" />
          <path d="M2.5 13.6h4.6V20" />
          <path d="M6.1 12.2 12 7.4l5.9 4.8" />
          <path d="M7.1 11.4V20h9.8v-8.6" />
          <path d="M10.7 20v-3.4h2.6V20" />
        </svg>
      );
    case "tweeonderkap":
      return (
        <svg {...gedeeld}>
          <path d="M2.5 20h19" />
          <path d="M2.8 12.6 12 6.6l9.2 6" />
          <path d="M4.2 11.7V20h15.6v-8.3" />
          <path d="M12 11.9V20" />
          <path d="M7.1 20v-3.2h2.4V20M14.5 20v-3.2h2.4V20" />
        </svg>
      );
    case "vrijstaand":
      return (
        <svg {...gedeeld}>
          <path d="M2.5 20h19" />
          <path d="M5.6 12.2 12 7.1l6.4 5.1" />
          <path d="M6.8 11.3V20h10.4v-8.7" />
          <path d="M10.7 20v-3.6h2.6V20" />
          <path d="M16 9.4V6.6h1.8v4.2" />
        </svg>
      );
    case "boerderij":
      return (
        <svg {...gedeeld}>
          <path d="M2 20h20" />
          <path d="M2.8 13.2C6 8.4 8.8 6.4 12 6.4s6 2 9.2 6.8" />
          <path d="M4.4 12.3V20h15.2v-7.7" />
          <path d="M9 20v-5.4h6V20" />
          <path d="M12 14.6V20" />
        </svg>
      );
    // Een huis met een boom ernaast, en een flat met een balkon: het verschil tussen twee antwoorden
    // in dezelfde rij zit hem precies in de buitenruimte, dus dat is wat de tekening laat zien.
    case "huistuin":
      return (
        <svg {...gedeeld}>
          <path d="M2 20h20" />
          <path d="M3.4 12.4 9 7.9l5.6 4.5" />
          <path d="M4.6 11.5V20h8.8v-8.5" />
          <path d="M7.8 20v-3.4h2.4V20" />
          <path d="M18.5 20v-3.4" />
          <path d="M18.5 16.6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
        </svg>
      );
    case "balkon":
      return (
        <svg {...gedeeld}>
          <path d="M3 20h18" />
          <path d="M6.5 20V4.5h11V20" />
          <path d="M9.2 7.6h1.6M13.2 7.6h1.6" />
          <path d="M6.5 12.2h11" />
          <path d="M8.3 12.2V16M11 12.2V16M13.7 12.2V16M16.4 12.2V16" />
          <path d="M6.5 16h11" />
        </svg>
      );
    default:
      return null;
  }
}
